import { logEvent, setUserId, setUserProperties, setConsent } from 'firebase/analytics';

const CONSENT_KEY = 'hh_analytics_consent';
const PAGE_START_KEY = '__hh_ga_page_start';
const ROUTE_KEY = '__hh_ga_route';

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  const matchers = [
    { name: 'Edge', re: /(Edg|Edge)\/([\d.]+)/ },
    { name: 'Chrome', re: /Chrome\/([\d.]+)/ },
    { name: 'Firefox', re: /Firefox\/([\d.]+)/ },
    { name: 'Safari', re: /Version\/([\d.]+).*Safari/ },
    { name: 'Opera', re: /(OPR|Opera)\/([\d.]+)/ },
    { name: 'IE', re: /(MSIE\s|Trident.*rv:)([\d.]+)/ },
  ];

  for (const matcher of matchers) {
    const m = ua.match(matcher.re);
    if (m) return { browserName: matcher.name, browserVersion: m[m.length - 1] || 'unknown' };
  }

  return { browserName: 'Unknown', browserVersion: 'unknown' };
};

const getOsName = () => {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Unknown';
};

const getDeviceCategory = () => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  return 'desktop';
};

const canTrack = (consentRequired) => {
  if (!consentRequired) return true;
  return localStorage.getItem(CONSENT_KEY) === 'granted';
};

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const route = useRoute();
  const config = useRuntimeConfig();
  const analytics = nuxtApp.$firebaseAnalytics;
  const consentRequired = String(config.public.ga_require_consent || 'true') === 'true';

  if (!analytics) return;

  const logPageDuration = (reason = 'route_change') => {
    if (!canTrack(consentRequired)) return;
    const start = window[PAGE_START_KEY];
    const path = window[ROUTE_KEY] || route.fullPath;
    if (!start || !path) return;

    const seconds = Math.max(1, Math.round((Date.now() - start) / 1000));
    logEvent(analytics, 'page_duration', {
      page_path: path,
      duration_seconds: seconds,
      exit_reason: reason,
    });
  };

  const trackPageView = (to) => {
    if (!canTrack(consentRequired)) return;

    const { browserName, browserVersion } = getBrowserInfo();
    const eventParams = {
      page_title: document.title || '',
      page_location: window.location.href,
      page_path: to.fullPath,
      page_referrer: document.referrer || '',
      engagement_time_msec: 1,
      device_category: getDeviceCategory(),
      browser_name: browserName,
      browser_version: browserVersion,
      os_name: getOsName(),
      language: navigator.language || 'unknown',
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    };

    logEvent(analytics, 'page_view', eventParams);
  };

  const trackEvent = (eventName, params = {}) => {
    if (!canTrack(consentRequired)) return;
    logEvent(analytics, eventName, params);
  };

  const setTrackingUser = (userId, userProps = {}) => {
    if (!canTrack(consentRequired)) return;
    if (userId) setUserId(analytics, String(userId));
    if (userProps && Object.keys(userProps).length) setUserProperties(analytics, userProps);
  };

  const grantConsent = () => {
    localStorage.setItem(CONSENT_KEY, 'granted');
    setConsent(analytics, {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    window[PAGE_START_KEY] = Date.now();
    window[ROUTE_KEY] = route.fullPath;
    trackPageView(route);
  };

  const denyConsent = () => {
    localStorage.setItem(CONSENT_KEY, 'denied');
    setConsent(analytics, {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  };

  if (!consentRequired && !localStorage.getItem(CONSENT_KEY)) {
    localStorage.setItem(CONSENT_KEY, 'granted');
  }

  if (canTrack(consentRequired)) {
    setConsent(analytics, {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });

    window[PAGE_START_KEY] = Date.now();
    window[ROUTE_KEY] = route.fullPath;
    trackPageView(route);
  }

  router.afterEach((to) => {
    logPageDuration('route_change');
    window[PAGE_START_KEY] = Date.now();
    window[ROUTE_KEY] = to.fullPath;
    trackPageView(to);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      logPageDuration('tab_hidden');
    } else if (document.visibilityState === 'visible') {
      window[PAGE_START_KEY] = Date.now();
      window[ROUTE_KEY] = route.fullPath;
    }
  });

  window.addEventListener('pagehide', () => logPageDuration('pagehide'));

  nuxtApp.provide('gaTrackEvent', trackEvent);
  nuxtApp.provide('gaSetUser', setTrackingUser);
  nuxtApp.provide('gaGrantConsent', grantConsent);
  nuxtApp.provide('gaDenyConsent', denyConsent);
  nuxtApp.provide('gaCanTrack', () => canTrack(consentRequired));
});
